/**
 * HrAdminPage — single-page workspace for users with the
 * `hr_admin.access` permission (or super-admins). Contains four
 * tabs:
 *
 *   1. Staff Directory  — UsAirPeople × tour progress × last login
 *   2. AI Studio        — Claude-powered material generator + chat
 *   3. Library          — saved materials, filter by language/type,
 *                         publish to /help (flag-only for now)
 *   4. Insights         — proactive HR action prompts
 *
 * The page is gated on the route in App.js but every action also
 * fails fast with a 401/403 from the backend so an admin who lost
 * the right mid-session sees a sensible error instead of a blank
 * tab.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Robot, BookOpen, Lightbulb, MagnifyingGlass,
  PaperPlaneRight, Sparkle, FloppyDisk, Trash, RocketLaunch,
  CheckCircle, ClockClockwise, Translate, Copy, ClipboardText,
  PlusCircle, Star,
} from "@phosphor-icons/react";
import {
  fetchHrStaff, fetchHrInsights, fetchHrLibrary, createHrLibraryItem,
  deleteHrLibraryItem, publishHrLibraryItem, generateHrMaterial,
  sendHrChatTurn, translateHrLibraryItem,
  fetchHrTemplates, createHrTemplate, deleteHrTemplate,
  fetchHrAssignments, createHrAssignments, deleteHrAssignment,
  hrMarkChecklistItem,
} from "@/lib/api";
import { useT } from "@/lib/i18n";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

const MATERIAL_TYPES = [
  { code: "orientation", label: "Orientation" },
  { code: "training",    label: "Training" },
  { code: "manual",      label: "Manual" },
  { code: "wizard",      label: "Wizard outline" },
  { code: "tutorial",    label: "Tutorial" },
];

const AUDIENCES = [
  { code: "staff",    label: "Internal staff" },
  { code: "dealer",   label: "Dealer technicians" },
  { code: "customer", label: "Customers" },
];

export default function HrAdminPage({ user }) {
  const t = useT();
  const [tab, setTab] = useState("staff");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Link to="/" data-testid="hr-back" className="text-slate-500 hover:text-slate-800">
            <ArrowLeft size={18} />
          </Link>
          <Sparkle size={18} weight="fill" className="text-indigo-600" />
          <h1 className="text-sm font-bold font-['Chivo'] tracking-tight">
            {t("hr.title", "HR Admin Workspace")}
          </h1>
          <div className="ml-auto text-xs text-slate-500 truncate max-w-[260px]">{user?.email}</div>
        </div>
        <nav className="max-w-6xl mx-auto flex gap-1 mt-3 border-b border-slate-100 -mb-3" data-testid="hr-tabs">
          {[
            { id: "staff",    icon: Users,         label: "Staff" },
            { id: "studio",   icon: Robot,         label: "AI Studio" },
            { id: "library",  icon: BookOpen,      label: "Library" },
            { id: "checklists", icon: ClipboardText, label: "Checklists" },
            { id: "insights", icon: Lightbulb,     label: "Insights" },
          ].map((it) => (
            <button key={it.id}
                    data-testid={`hr-tab-${it.id}`}
                    onClick={() => setTab(it.id)}
                    className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-t-sm border-b-2 inline-flex items-center gap-1.5 ${
                      tab === it.id
                        ? "border-indigo-600 text-indigo-700 bg-indigo-50"
                        : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                    }`}>
              <it.icon size={13} weight="bold" /> {it.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        {tab === "staff"    && <StaffTab />}
        {tab === "studio"   && <StudioTab user={user} onSaved={() => toast.success("Saved to library")} />}
        {tab === "library"  && <LibraryTab />}
        {tab === "checklists" && <ChecklistsTab user={user} />}
        {tab === "insights" && <InsightsTab />}
      </main>
    </div>
  );
}


/* ─── Staff tab ──────────────────────────────────────────────── */
function StaffTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchHrStaff()
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load staff"));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.rows;
    return data.rows.filter((r) =>
      (r.email || "").toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle) ||
      (r.position || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  if (err) return <ErrorPane msg={err} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-4" data-testid="hr-staff-tab">
      {/* Summary */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Staff" value={data.summary.total_staff} tone="indigo" />
        <SummaryCard label="Onboarding done" value={data.summary.onboarding_completed} tone="emerald" />
        <SummaryCard label="In progress"      value={data.summary.onboarding_in_progress} tone="amber" />
        <SummaryCard label="Inactive 30d+"    value={data.summary.inactive_30d} tone="rose" />
      </section>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="hr-staff-search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Filter by name, email, or position…"
               className="w-full pl-9 pr-3 py-2 rounded-sm border border-slate-300 text-sm" />
      </div>

      {/* Table */}
      <div className="overflow-auto bg-white border border-slate-200 rounded-md">
        <table className="w-full text-sm" data-testid="hr-staff-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left p-2">Person</th>
              <th className="text-left p-2">Position / Company</th>
              <th className="text-left p-2">Last login</th>
              <th className="text-left p-2">Tour progress</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.email} data-testid={`hr-staff-row-${r.email}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-2">
                  <div className="font-semibold text-slate-800">{r.name || r.email}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{r.email}</div>
                </td>
                <td className="p-2">
                  <div className="text-xs text-slate-700">{r.position || "—"}</div>
                  <div className="text-[11px] text-slate-500">{r.company || ""}</div>
                </td>
                <td className="p-2 text-xs">
                  {r.last_login_at ? (
                    <>
                      <div>{(r.last_login_at || "").slice(0, 10)}</div>
                      <div className="text-[11px] text-slate-500">{r.days_since_login}d ago</div>
                    </>
                  ) : <span className="text-slate-400 italic">never</span>}
                </td>
                <td className="p-2 w-40">
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 flex-1 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500"
                           style={{ width: `${(r.tours_completed / Math.max(1, r.tours_total)) * 100}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-slate-600 shrink-0">
                      {r.tours_completed}/{r.tours_total}
                    </span>
                  </div>
                </td>
                <td className="p-2">
                  <StatusPill status={r.onboarding_status} />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-sm text-slate-400 italic">No staff match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── AI Studio tab ──────────────────────────────────────────── */
function StudioTab({ user, onSaved }) {
  const [form, setForm] = useState({
    material_type: "orientation",
    topic: "",
    audience: ["staff"],
    language: detectUiLanguage(),
    tone: "friendly",
    extra_context: "",
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [chat, setChat] = useState({ session_id: "", history: [] });
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Tell me what to write about first."); return; }
    setGenerating(true); setResult(null);
    try {
      const r = await generateHrMaterial(form);
      setResult(r);
      if (r.fallback) toast.warning("AI unavailable — generated a scaffold instead.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await createHrLibraryItem({
        title: result.title,
        material_type: form.material_type,
        language: form.language,
        audience: form.audience,
        body: result.body,
        tags: [],
        notes: form.topic,
      });
      onSaved?.();
      setResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput(""); setChatBusy(true);
    try {
      const r = await sendHrChatTurn({
        session_id: chat.session_id || undefined,
        message: msg,
        language: form.language,
      });
      setChat({ session_id: r.session_id, history: r.history });
      if (r.fallback) toast.warning("AI offline — sent a scaffolded reply.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chat failed");
    } finally { setChatBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hr-studio-tab">
      {/* Generation form + result */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-indigo-600" />
          <h2 className="text-sm font-bold">Generate material</h2>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Pick label="Type" value={form.material_type} onChange={set("material_type")}
                options={MATERIAL_TYPES.map((m) => [m.code, m.label])}
                testId="hr-studio-type" />
          <Pick label="Language" value={form.language} onChange={set("language")}
                options={LANGUAGES.map((l) => [l.code, l.label])}
                testId="hr-studio-language" icon={Translate} />
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Audience</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {AUDIENCES.map((a) => {
              const on = form.audience.includes(a.code);
              return (
                <button key={a.code}
                        data-testid={`hr-studio-audience-${a.code}`}
                        onClick={() => set("audience")(on ? form.audience.filter((x) => x !== a.code) : [...form.audience, a.code])}
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${
                          on ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}>
                  {a.label}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Topic"
               testId="hr-studio-topic"
               value={form.topic} onChange={set("topic")}
               placeholder="e.g. First-day orientation for new compressor service technicians" />

        <Field label="Extra context (optional)"
               testId="hr-studio-context"
               value={form.extra_context} onChange={set("extra_context")}
               textarea rows={3}
               placeholder="Add any specifics — equipment focus, certifications, dealer audience, etc." />

        <div className="flex items-center gap-2 pt-1">
          <Pick label="Tone" value={form.tone} onChange={set("tone")} compact
                options={[["friendly","Friendly"],["formal","Formal"],["concise","Concise"]]}
                testId="hr-studio-tone" />
          <button onClick={generate} disabled={generating}
                  data-testid="hr-studio-generate"
                  className="ml-auto inline-flex items-center gap-1 px-3 py-2 rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {generating ? <ClockClockwise size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
            {generating ? "Generating…" : "Generate"}
          </button>
        </div>

        {result && (
          <div className="border-t border-slate-200 pt-3 mt-3 space-y-2" data-testid="hr-studio-result">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold flex-1 truncate">{result.title}</h3>
              {result.fallback && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">Scaffold</span>}
              <button onClick={() => navigator.clipboard.writeText(result.body)}
                      data-testid="hr-studio-copy"
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                <Copy size={10} /> Copy
              </button>
              <button onClick={save} disabled={saving}
                      data-testid="hr-studio-save"
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                <FloppyDisk size={10} /> {saving ? "Saving…" : "Save to library"}
              </button>
            </div>
            <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-72 overflow-auto leading-relaxed">{result.body}</pre>
          </div>
        )}
      </section>

      {/* Chat */}
      <section className="bg-white border border-slate-200 rounded-md p-4 flex flex-col" data-testid="hr-studio-chat">
        <div className="flex items-center gap-2">
          <Robot size={14} weight="bold" className="text-indigo-600" />
          <h2 className="text-sm font-bold">Ask the HR AI</h2>
          <span className="ml-auto text-[10px] font-mono text-slate-400">{chat.session_id ? `session ${chat.session_id.slice(0,8)}` : "new session"}</span>
        </div>

        <div className="flex-1 mt-2 min-h-[300px] max-h-[480px] overflow-auto bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2">
          {chat.history.length === 0 && (
            <p className="text-xs text-slate-500 italic">
              Ask anything — "draft a 5-step onboarding wizard for new dealer techs", "translate this manual to Spanish", "what should I cover in week-one orientation?".
            </p>
          )}
          {chat.history.map((m, i) => (
            <div key={i} className={`text-xs leading-relaxed ${m.role === "user" ? "text-slate-800" : "text-indigo-900"}`}
                 data-testid={`hr-chat-msg-${i}`}>
              <div className="text-[9px] font-bold uppercase tracking-wider opacity-60">{m.role}</div>
              <pre className="whitespace-pre-wrap font-sans">{m.text}</pre>
            </div>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          <input data-testid="hr-chat-input"
                 value={chatInput}
                 onChange={(e) => setChatInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }}}
                 placeholder="Ask the HR AI…"
                 className="flex-1 px-3 py-2 rounded-sm border border-slate-300 text-sm" />
          <button onClick={sendChat} disabled={chatBusy || !chatInput.trim()}
                  data-testid="hr-chat-send"
                  className="px-3 py-2 rounded-sm bg-indigo-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1">
            <PaperPlaneRight size={12} weight="bold" /> Send
          </button>
        </div>
      </section>
    </div>
  );
}


/* ─── Library tab ────────────────────────────────────────────── */
function LibraryTab() {
  const [items, setItems] = useState(null);
  const [filterLang, setFilterLang] = useState("");
  const [filterType, setFilterType] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => {
    const params = {};
    if (filterLang) params.language = filterLang;
    if (filterType) params.material_type = filterType;
    fetchHrLibrary(params).then(setItems).catch(() => setItems([]));
  };
  useEffect(load, [filterLang, filterType]);

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this library item permanently?")) return;
    setBusyId(id);
    try { await deleteHrLibraryItem(id); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(""); }
  };
  const handlePublish = async (id) => {
    setBusyId(id);
    try { await publishHrLibraryItem(id); load(); toast.success("Published — visible in /help next iteration"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusyId(""); }
  };
  const handleTranslate = async (id, isPublished) => {
    setBusyId(id);
    try {
      const r = await translateHrLibraryItem(id);
      load();
      const created = r.created_ids?.length || 0;
      const skipped = r.skipped?.length || 0;
      const tail = isPublished ? " (auto-published)" : " (saved as drafts)";
      if (r.fallback) toast.warning(`Translated with fallback content — ${created} created${tail}. Review them.`);
      else toast.success(`Translated — ${created} new language(s)${created ? tail : ""}${skipped ? `, ${skipped} skipped` : ""}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Translate failed"); }
    finally { setBusyId(""); }
  };

  if (!items) return <Loading />;
  return (
    <div className="space-y-3" data-testid="hr-library-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Pick label="Language" value={filterLang} onChange={setFilterLang} compact
              options={[["", "All"], ...LANGUAGES.map((l) => [l.code, l.label])]}
              testId="hr-library-lang" />
        <Pick label="Type" value={filterType} onChange={setFilterType} compact
              options={[["", "All"], ...MATERIAL_TYPES.map((m) => [m.code, m.label])]}
              testId="hr-library-type" />
        <span className="ml-auto text-[11px] text-slate-500">{items.length} item(s)</span>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500">
          No materials yet — head to <b>AI Studio</b> to generate your first one.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id} data-testid={`hr-library-row-${it.id}`}
                className="bg-white border border-slate-200 rounded-md p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-slate-800">{it.title}</h3>
                    <Tag tone="indigo">{MATERIAL_TYPES.find((m) => m.code === it.material_type)?.label || it.material_type}</Tag>
                    <Tag tone="amber">{LANGUAGES.find((l) => l.code === it.language)?.label || it.language}</Tag>
                    {it.published
                      ? <Tag tone="emerald"><CheckCircle size={9} weight="fill" className="inline mr-0.5" /> Published</Tag>
                      : <Tag tone="slate">Draft</Tag>}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Created {(it.created_at || "").slice(0, 10)} by {it.created_by}
                    {it.audience?.length > 0 ? ` · for ${it.audience.join(", ")}` : ""}
                  </p>
                  <details className="mt-2">
                    <summary className="text-[11px] text-indigo-700 cursor-pointer">Preview body</summary>
                    <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 rounded-sm p-2 mt-1 max-h-48 overflow-auto">{it.body}</pre>
                  </details>
                </div>
                <div className="flex gap-2">
                  {!it.published && (
                    <button onClick={() => handlePublish(it.id)} disabled={busyId === it.id}
                            data-testid={`hr-library-publish-${it.id}`}
                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                      <RocketLaunch size={10} weight="bold" /> Publish
                    </button>
                  )}
                  <button onClick={() => handleTranslate(it.id, it.published)} disabled={busyId === it.id}
                          data-testid={`hr-library-translate-${it.id}`}
                          title="Generate FR + ES (or whichever are missing) with Claude"
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50 inline-flex items-center gap-1">
                    <Translate size={10} weight="bold" /> Translate
                  </button>
                  <button onClick={() => handleDelete(it.id)} disabled={busyId === it.id}
                          data-testid={`hr-library-delete-${it.id}`}
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1">
                    <Trash size={10} /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* ─── Insights tab ───────────────────────────────────────────── */
function InsightsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetchHrInsights().then(setData).catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, []);
  if (err) return <ErrorPane msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-2" data-testid="hr-insights-tab">
      <p className="text-[11px] text-slate-500">Generated {data.generated_at?.slice(0, 19).replace("T", " ")} UTC</p>
      <ul className="space-y-2">
        {data.items.map((it, i) => (
          <li key={i} data-testid={`hr-insight-${i}`}
              className={`rounded-md border p-3 flex items-start gap-2 ${
                it.severity === "critical" ? "bg-red-50 border-red-200"
                : it.severity === "warning" ? "bg-amber-50 border-amber-200"
                : "bg-blue-50 border-blue-200"
              }`}>
            <Lightbulb size={14} weight="fill" className={
              it.severity === "critical" ? "text-red-600"
              : it.severity === "warning" ? "text-amber-600"
              : "text-blue-600"
            } />
            <div>
              <div className="text-sm font-bold text-slate-800">{it.title}</div>
              <p className="text-xs text-slate-700 mt-1">{it.detail}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* ─── Checklists tab ──────────────────────────────────────────── */
function ChecklistsTab({ user }) {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [building, setBuilding] = useState(false);
  const [name, setName] = useState("");
  const [items, setItems] = useState([{ type: "tour_watch", label: "", ref_id: "" }]);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignTemplateId, setAssignTemplateId] = useState("");
  const [assignEmails, setAssignEmails] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [busy, setBusy] = useState("");

  const reload = () => {
    fetchHrTemplates().then(setTemplates).catch(() => setTemplates([]));
    fetchHrAssignments().then(setAssignments).catch(() => setAssignments([]));
  };
  useEffect(reload, []);

  const addItem = () => setItems((arr) => [...arr, { type: "acknowledge", label: "", ref_id: "" }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const updItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const saveTemplate = async () => {
    if (!name.trim() || !items.length || items.some((i) => !i.label.trim())) {
      toast.error("Name + at least one item with a label required");
      return;
    }
    setSaving(true);
    try {
      await createHrTemplate({ name, description: "", items, is_default: isDefault });
      toast.success("Template saved");
      setBuilding(false); setName(""); setItems([{ type: "tour_watch", label: "", ref_id: "" }]); setIsDefault(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const removeTemplate = async (tid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete template? All assignments using it will also be removed.")) return;
    setBusy(tid);
    try { await deleteHrTemplate(tid); reload(); toast.success("Template deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };

  const assignBulk = async () => {
    const emails = assignEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!assignTemplateId || emails.length === 0) {
      toast.error("Pick a template and add at least one email");
      return;
    }
    setAssigning(true);
    try {
      await createHrAssignments({ template_id: assignTemplateId, emails });
      reload();
      setAssignEmails("");
      toast.success(`Assigned to ${emails.length} employee(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setAssigning(false); }
  };

  const removeAssignment = async (aid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this assignment? The employee will lose their checklist.")) return;
    setBusy(aid);
    try { await deleteHrAssignment(aid); reload(); toast.success("Assignment removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(""); }
  };

  const hrMark = async (assignment_id, item_id, done) => {
    setBusy(`${assignment_id}-${item_id}`);
    try {
      await hrMarkChecklistItem({ assignment_id, item_id, done });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mark failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="hr-checklists-tab">
      {/* Templates */}
      <section className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ClipboardText size={14} weight="bold" className="text-indigo-600" />
          <h2 className="text-sm font-bold">Templates</h2>
          <span className="text-[11px] text-slate-500">{templates.length}</span>
          <button onClick={() => setBuilding((b) => !b)}
                  data-testid="hr-checklists-new-template"
                  className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1">
            <PlusCircle size={10} weight="bold" /> New template
          </button>
        </div>

        {building && (
          <div className="mt-3 border border-indigo-200 bg-indigo-50/40 rounded-sm p-3 space-y-2"
               data-testid="hr-checklists-builder">
            <input data-testid="hr-checklists-template-name"
                   value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Template name (e.g. New hire — week 1)"
                   className="w-full px-3 py-2 rounded-sm border border-slate-300 text-sm" />

            <div className="space-y-1.5">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2"
                     data-testid={`hr-checklists-item-row-${i}`}>
                  <select value={it.type} onChange={(e) => updItem(i, "type", e.target.value)}
                          className="px-2 py-1.5 rounded-sm border border-slate-300 text-xs">
                    <option value="library_read">Read library</option>
                    <option value="tour_watch">Watch tour</option>
                    <option value="acknowledge">Self-acknowledge</option>
                    <option value="hr_marked">HR-marked</option>
                  </select>
                  <input value={it.label} onChange={(e) => updItem(i, "label", e.target.value)}
                         placeholder="Label shown to employee"
                         className="flex-1 px-2 py-1.5 rounded-sm border border-slate-300 text-xs" />
                  {(it.type === "library_read" || it.type === "tour_watch") && (
                    <input value={it.ref_id} onChange={(e) => updItem(i, "ref_id", e.target.value)}
                           placeholder={it.type === "tour_watch" ? "tour storage key" : "library item id"}
                           className="w-40 px-2 py-1.5 rounded-sm border border-slate-300 text-xs font-mono" />
                  )}
                  <button onClick={() => removeItem(i)} title="Remove item"
                          className="text-slate-400 hover:text-red-600 px-1">
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addItem}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
              <PlusCircle size={10} /> Add item
            </button>

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}
                     data-testid="hr-checklists-is-default" />
              <Star size={11} weight="fill" className="text-amber-500" />
              Set as default template (auto-assigned to new staff)
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setBuilding(false)}
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
              <button onClick={saveTemplate} disabled={saving}
                      data-testid="hr-checklists-save-template"
                      className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {saving ? "Saving…" : "Save template"}
              </button>
            </div>
          </div>
        )}

        {templates.length > 0 && (
          <ul className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-sm">
            {templates.map((t) => (
              <li key={t.id} data-testid={`hr-checklists-template-${t.id}`}
                  className="p-2 flex items-center gap-2">
                {t.is_default && <Star size={11} weight="fill" className="text-amber-500" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-[11px] text-slate-500">{t.items.length} items</div>
                </div>
                <button onClick={() => removeTemplate(t.id)} disabled={busy === t.id}
                        data-testid={`hr-checklists-delete-template-${t.id}`}
                        className="text-[10px] text-red-700 hover:bg-red-50 px-2 py-1 rounded-sm">
                  <Trash size={10} className="inline" /> Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Assign + matrix */}
      <section className="bg-white border border-slate-200 rounded-md p-4">
        <h2 className="text-sm font-bold mb-2">Assign a template</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select value={assignTemplateId} onChange={(e) => setAssignTemplateId(e.target.value)}
                  data-testid="hr-checklists-assign-template"
                  className="px-2 py-1.5 rounded-sm border border-slate-300 text-xs">
            <option value="">Pick a template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input data-testid="hr-checklists-assign-emails"
                 value={assignEmails} onChange={(e) => setAssignEmails(e.target.value)}
                 placeholder="Comma or space-separated emails"
                 className="px-2 py-1.5 rounded-sm border border-slate-300 text-xs sm:col-span-2" />
        </div>
        <button onClick={assignBulk} disabled={assigning}
                data-testid="hr-checklists-assign-go"
                className="mt-2 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          {assigning ? "Assigning…" : "Assign"}
        </button>

        <h3 className="text-xs font-bold text-slate-700 mt-5 mb-1">
          Assignments ({assignments.length})
        </h3>
        {assignments.length === 0 ? (
          <p className="text-[11px] text-slate-500 italic">No assignments yet. Use the form above to assign a template, or set one as the default to auto-assign new staff.</p>
        ) : (
          <ul className="space-y-2">
            {assignments.map((a) => (
              <li key={a.id} data-testid={`hr-checklists-assignment-${a.id}`}
                  className="border border-slate-200 rounded-sm p-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-xs">{a.email}</div>
                  <span className="text-[10px] text-slate-500">— {a.template_name}</span>
                  <span className="ml-auto text-[10px] font-mono text-slate-700">
                    {a.completed_count}/{a.total} ({a.percent}%)
                  </span>
                  <button onClick={() => removeAssignment(a.id)} disabled={busy === a.id}
                          className="text-[10px] text-red-700 hover:bg-red-50 px-2 py-0.5 rounded-sm">
                    <Trash size={10} className="inline" />
                  </button>
                </div>
                <div className="h-1 mt-1 mb-2 bg-slate-200 rounded overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${a.percent}%` }} />
                </div>
                <ul className="space-y-1">
                  {a.items.map((it) => (
                    <li key={it.item_id} className="flex items-center gap-2 text-xs">
                      {it.completed
                        ? <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                        : <Sparkle size={11} className="text-slate-300" />}
                      <span className={it.completed ? "text-slate-500 line-through" : "text-slate-800"}>
                        {it.label}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-slate-400">
                        {it.type.replace("_", " ")}
                      </span>
                      {it.type === "hr_marked" && !it.completed && (
                        <button onClick={() => hrMark(a.id, it.item_id, true)} disabled={busy === `${a.id}-${it.item_id}`}
                                data-testid={`hr-checklists-mark-${a.id}-${it.item_id}`}
                                className="ml-auto text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-600 text-white hover:bg-indigo-700">
                          Mark done
                        </button>
                      )}
                      {it.type === "hr_marked" && it.completed && (
                        <button onClick={() => hrMark(a.id, it.item_id, false)} disabled={busy === `${a.id}-${it.item_id}`}
                                className="ml-auto text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-slate-200 hover:bg-slate-50">
                          Undo
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}


/* ─── Reusable bits ──────────────────────────────────────────── */
function Loading() {
  return <div className="flex items-center justify-center p-12 text-sm text-slate-500"><ClockClockwise size={16} className="animate-spin mr-2" /> Loading…</div>;
}

function ErrorPane({ msg }) {
  return <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700" data-testid="hr-error">{msg}</div>;
}

function SummaryCard({ label, value, tone }) {
  const colors = {
    indigo:  "border-indigo-200 bg-indigo-50 text-indigo-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-700",
    rose:    "border-rose-200 bg-rose-50 text-rose-700",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`border rounded-md p-3 ${colors}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-['Chivo'] mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed:    ["bg-emerald-100 text-emerald-700 border-emerald-200", "Completed"],
    in_progress:  ["bg-amber-100 text-amber-700 border-amber-200", "In progress"],
    not_started:  ["bg-slate-100 text-slate-600 border-slate-200", "Not started"],
  }[status] || ["bg-slate-100 text-slate-600 border-slate-200", status];
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${map[0]}`}>{map[1]}</span>;
}

function Tag({ children, tone }) {
  const map = {
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate:   "bg-slate-100 text-slate-600 border-slate-200",
  }[tone] || "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${map}`}>{children}</span>;
}

function Pick({ label, value, onChange, options, compact, icon: Icon, testId }) {
  return (
    <label className={compact ? "inline-flex items-center gap-1" : "block"}>
      {!compact && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
        {Icon && <Icon size={10} weight="bold" className="inline mr-1" />}{label}
      </span>}
      {compact && Icon && <Icon size={10} weight="bold" className="text-slate-400" />}
      {compact && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>}
      <select data-testid={testId}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={`px-2 py-1.5 rounded-sm border border-slate-300 text-xs ${compact ? "" : "w-full"}`}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function Field({ label, value, onChange, placeholder, textarea, rows, testId }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
      <Tag data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
           placeholder={placeholder} rows={rows}
           className="w-full px-3 py-2 rounded-sm border border-slate-300 text-sm" />
    </label>
  );
}

function detectUiLanguage() {
  try {
    const lang = (localStorage.getItem("usair_lang") || navigator.language || "en").slice(0, 2).toLowerCase();
    return ["en", "fr", "es"].includes(lang) ? lang : "en";
  } catch { return "en"; }
}
